import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Button, Input, Spin, Tooltip, message } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { SetterOrUpdater } from 'recoil'
import { ContentType } from 'common/src/commentThread/types'
import { ContentPath } from 'common/src/ContentPath'
import { CommentThreadEditor } from 'magic-text-box-lexical/src/plugins/ReadOnlyAnnotatedContentPlugin/CommentThreadEditor/CommentThreadEditor'
import { CommentMetadata } from 'magic-text-box-lexical/src/context/AnnotatedContentContext/AnnotatedContent.types'
import {
    CommentDraftStatus,
    CommentReplyDraftPublicState,
} from 'common/src/api/commentReplyDraft/types'
import {
    getCommentReplyDraft,
    overrideCommentReplyDraft,
    recordCommentReplyPosted,
    redraftCommentReplyDraft,
    setCommentReplyDraftMuted,
} from '@/api/cb/commentDraft'
import {
    addCommentToThread,
    draftReplyForComment,
} from '@/api/wb/commentThread'
import { fetchComments } from '@/atoms/contentCommentThreads/utils'
import styles from './CommentReplyDraft.module.less'
import {
    RepliedCommentInfo,
    useClearCommentNotificationPrompt,
} from '@/hooks/useClearCommentNotificationPrompt'

import {
    draftMarkdownToCommentEditorState,
    editorStateToMarkdown,
} from './draftMarkdown'

const POLL_MS = 2_000
const IN_FLIGHT: CommentDraftStatus[] = [
    CommentDraftStatus.classifying,
    CommentDraftStatus.drafting,
]

// Eupheus's suggested reply to a student comment, shown inline beneath it in
// the content editor. Poll while the draft is in flight; let the instructor
// edit it in the comment editor and post it (refreshing the thread + tracking
// edits), or override a "skip" verdict.
export const CommentReplyDraft: React.FC<{
    commentId: string
    courseName: string
    contentType: ContentType
    contentId: string
    contentPath: ContentPath
    setCommentMetadataArray?: SetterOrUpdater<CommentMetadata[]>
    // Called after a reply is posted, so hosts that render their own comment
    // list (e.g. the outline annotation panel) can refresh it.
    onPosted?: () => void | Promise<void>
    // The student comment this draft answers; lets a posted reply offer to
    // clear that comment's row from the notification queue.
    comment?: RepliedCommentInfo
}> = (props): ReactElement | null => {
    const [draft, setDraft] = useState<CommentReplyDraftPublicState | null>(
        null
    )
    const [loaded, setLoaded] = useState(false)
    // The editor's current content (stringified editor state); seeded from
    // the draft and updated as the instructor edits.
    const [editedState, setEditedState] = useState('')
    const [isPosting, setIsPosting] = useState(false)
    const [isOverriding, setIsOverriding] = useState(false)
    const [isMuting, setIsMuting] = useState(false)
    const [isRequesting, setIsRequesting] = useState(false)
    const [isRedrafting, setIsRedrafting] = useState(false)
    const [showRedraft, setShowRedraft] = useState(false)
    const [redraftNote, setRedraftNote] = useState('')
    const promptToClearNotification = useClearCommentNotificationPrompt()

    const load = useCallback(async (): Promise<void> => {
        const res = await getCommentReplyDraft({
            courseName: props.courseName,
            commentId: props.commentId,
        })
        setLoaded(true)
        if (res.data.success) setDraft(res.data.payload.draft)
    }, [props.courseName, props.commentId])

    useEffect(() => {
        void load()
    }, [load])

    // Poll while classifying/drafting.
    useEffect(() => {
        if (!draft || !IN_FLIGHT.includes(draft.status)) return
        const timer = window.setInterval((): void => {
            void load()
        }, POLL_MS)
        return () => window.clearInterval(timer)
    }, [draft, load])

    // The draft as the comment editor loads it. A redraft changes draftText,
    // which re-seeds the editor (and re-keys it, below).
    const draftText =
        draft?.status === CommentDraftStatus.ready ? draft.draftText : null
    const initialEditorState = useMemo(
        (): string =>
            draftText ? draftMarkdownToCommentEditorState(draftText) : '',
        [draftText]
    )
    useEffect(() => {
        setEditedState(initialEditorState)
    }, [initialEditorState])
    const editorKey = `${props.commentId}-${draft?.previousDrafts.length ?? 0}`

    const refreshThread = useCallback(async (): Promise<void> => {
        if (!props.setCommentMetadataArray) return
        const updated = await fetchComments({
            contentId: props.contentId,
            contentType: props.contentType,
            courseName: props.courseName,
        })
        if (updated) props.setCommentMetadataArray(updated)
    }, [props])

    const post = useCallback(async (): Promise<void> => {
        if (!draft || !editedState) return
        setIsPosting(true)
        try {
            const res = await addCommentToThread({
                contentType: props.contentType,
                contentId: props.contentId,
                text: editedState,
                isAnonymous: false,
                courseName: props.courseName,
                contentPath: props.contentPath,
            })
            if (!res.data.success) {
                message.error('Could not post the reply.')
                return
            }
            const finalText =
                editorStateToMarkdown(editedState) || draft.draftText || ''
            const originalText = editorStateToMarkdown(initialEditorState)
            await recordCommentReplyPosted({
                courseName: props.courseName,
                commentId: props.commentId,
                finalText,
                wasEdited: finalText !== originalText,
            })
            message.success('Reply posted')
            await refreshThread()
            await props.onPosted?.()
            await load()
            await promptToClearNotification({
                courseName: props.courseName,
                contentType: props.contentType,
                contentId: props.contentId,
                comment: props.comment,
            })
        } finally {
            setIsPosting(false)
        }
    }, [
        draft,
        editedState,
        initialEditorState,
        props,
        refreshThread,
        load,
        promptToClearNotification,
    ])

    const override = useCallback(async (): Promise<void> => {
        setIsOverriding(true)
        try {
            const res = await overrideCommentReplyDraft({
                courseName: props.courseName,
                commentId: props.commentId,
            })
            if (res.data.success) setDraft(res.data.payload.draft)
        } finally {
            setIsOverriding(false)
        }
    }, [props.courseName, props.commentId])

    // "No reply necessary" (mute) and its undo. A drafted reply is kept
    // server-side, so restoring brings it back ready to use; muting a comment
    // that has no draft yet records the decision alone, and undoing it returns
    // the comment to offering "Draft reply" (the response carries no draft).
    const setMuted = useCallback(
        async (muted: boolean): Promise<void> => {
            setIsMuting(true)
            try {
                const res = await setCommentReplyDraftMuted({
                    courseName: props.courseName,
                    commentId: props.commentId,
                    muted,
                    stubContext: {
                        contentType: props.contentType,
                        contentId: props.contentId,
                        contentPath: props.contentPath,
                    },
                })
                if (res.data.success) setDraft(res.data.payload.draft)
                else message.error('Could not update the draft.')
            } finally {
                setIsMuting(false)
            }
        },
        [
            props.courseName,
            props.commentId,
            props.contentType,
            props.contentId,
            props.contentPath,
        ]
    )

    // Retroactively ask for a draft on a student comment that has none yet
    // (e.g. it predates the eager relay). WB re-reads the comment + thread and
    // enqueues on CB; the returned classifying draft turns on polling.
    const requestDraft = useCallback(async (): Promise<void> => {
        setIsRequesting(true)
        try {
            const res = await draftReplyForComment({
                courseName: props.courseName,
                contentType: props.contentType,
                contentId: props.contentId,
                contentPath: props.contentPath,
                commentId: props.commentId,
            })
            if (res.data.success) {
                setDraft(res.data.payload.draft)
            } else {
                message.error('Could not start a draft.')
            }
        } finally {
            setIsRequesting(false)
        }
    }, [props])

    // Throw this draft away and generate another. The note is optional, but a
    // blind re-roll only re-samples the same distribution — when the
    // instructor can say what was wrong, saying it is what actually helps.
    const redraft = useCallback(
        async (guidance?: string): Promise<void> => {
            setIsRedrafting(true)
            try {
                const res = await redraftCommentReplyDraft({
                    courseName: props.courseName,
                    commentId: props.commentId,
                    ...(guidance ? { guidance } : {}),
                })
                if (res.data.success) {
                    // The new draft re-seeds the editor; the in-flight status
                    // restarts polling.
                    setShowRedraft(false)
                    setRedraftNote('')
                    setDraft(res.data.payload.draft)
                } else {
                    // Surfaces the server's own words — the cooldown and the
                    // "already posted" guard both explain themselves.
                    message.error(res.data.error || 'Could not redraft.')
                }
            } finally {
                setIsRedrafting(false)
            }
        },
        [props.courseName, props.commentId]
    )

    // Copies the reply as markdown (with any edits), for pasting elsewhere.
    const copy = useCallback(() => {
        const markdown =
            (editedState ? editorStateToMarkdown(editedState) : '') ||
            draft?.draftText ||
            ''
        if (!markdown) return
        void navigator.clipboard
            .writeText(markdown)
            .then(() => message.success('Draft copied'))
            .catch(() => message.error('Could not copy'))
    }, [editedState, draft])

    if (!loaded) return null

    // No draft yet (e.g. a comment from before the eager relay): offer to
    // draft one on demand, or to set it aside without drafting anything.
    if (!draft)
        return (
            <div className={styles.draftBox}>
                <div className={styles.actions}>
                    <Button
                        size={'small'}
                        loading={isRequesting}
                        onClick={() => void requestDraft()}
                    >
                        ✦ Draft reply
                    </Button>
                    <Tooltip
                        title={
                            'Set this comment aside without drafting — you can undo it later'
                        }
                    >
                        <Button
                            size={'small'}
                            loading={isMuting}
                            onClick={() => void setMuted(true)}
                        >
                            No reply necessary
                        </Button>
                    </Tooltip>
                </div>
            </div>
        )

    return (
        <div className={styles.draftBox}>
            <div className={styles.header}>
                <span className={styles.label}>✦ Eupheus draft reply</span>
            </div>

            {IN_FLIGHT.includes(draft.status) && (
                <div className={styles.inFlight}>
                    <Spin indicator={<LoadingOutlined spin />} />{' '}
                    {draft.status === CommentDraftStatus.classifying
                        ? 'Deciding if this needs a reply…'
                        : 'Drafting a reply…'}
                </div>
            )}

            {draft.status === CommentDraftStatus.skipped && (
                <div className={styles.skipped}>
                    <span>
                        Eupheus didn&apos;t think this needs a reply
                        {draft.verdictReason ? ` — ${draft.verdictReason}` : ''}
                        .
                    </span>
                    <Button
                        size={'small'}
                        loading={isOverriding}
                        onClick={() => void override()}
                    >
                        Draft anyway
                    </Button>
                </div>
            )}

            {draft.status === CommentDraftStatus.failed && (
                <div className={styles.skipped}>
                    <span>Drafting hit a snag.</span>
                    <Button
                        size={'small'}
                        loading={isOverriding}
                        onClick={() => void override()}
                    >
                        Try again
                    </Button>
                </div>
            )}

            {draft.status === CommentDraftStatus.muted && (
                <div className={styles.muted}>
                    <span>Muted — you marked this as needing no reply.</span>
                    <Button
                        size={'small'}
                        loading={isMuting}
                        onClick={() => void setMuted(false)}
                    >
                        {draft.draftText ? 'Restore draft' : 'Undo'}
                    </Button>
                </div>
            )}

            {draft.status === CommentDraftStatus.posted && (
                <div className={styles.posted}>Reply posted ✓</div>
            )}

            {draft.status === CommentDraftStatus.ready && (
                <>
                    {/* The same editor as the thread's composer, so formatting
                        shortcuts, equations and links all work on the draft,
                        and what you see is what gets posted. */}
                    <div className={styles.editor}>
                        <CommentThreadEditor
                            id={editorKey}
                            placeholder={'Draft reply'}
                            minHeightInPx={120}
                            initialEditorState={initialEditorState}
                            onChange={setEditedState}
                            hideSubmitRow={true}
                            cannotPostAnonymously={true}
                            canInsertUniversalContentLink={true}
                        />
                    </div>
                    <div className={styles.editorHint}>
                        To quote an earlier comment, paste it in, select it and
                        press Ctrl+Q.
                    </div>
                    <div className={styles.actions}>
                        <Button
                            type={'primary'}
                            loading={isPosting}
                            disabled={!editedState}
                            onClick={() => void post()}
                        >
                            Post reply
                        </Button>
                        <Tooltip
                            title={
                                'Copy the reply as markdown to your clipboard'
                            }
                        >
                            <Button onClick={copy}>Copy</Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                'Generate a different reply. Your current draft is kept.'
                            }
                        >
                            <Button
                                onClick={() => setShowRedraft((v) => !v)}
                                disabled={isRedrafting}
                            >
                                Redraft
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                'Mute this draft without replying — you can restore it later'
                            }
                        >
                            <Button
                                loading={isMuting}
                                onClick={() => void setMuted(true)}
                            >
                                No reply necessary
                            </Button>
                        </Tooltip>
                        {draft.previousDrafts.length > 0 && (
                            <span className={styles.attempts}>
                                {draft.previousDrafts.length + 1} attempts
                            </span>
                        )}
                    </div>

                    {showRedraft && (
                        <div className={styles.redraftPanel}>
                            <div className={styles.redraftHint}>
                                What should be different? Telling Eupheus what
                                the draft got wrong works better than a plain
                                re-roll.
                            </div>
                            <Input.TextArea
                                className={styles.noteInput}
                                value={redraftNote}
                                placeholder={
                                    'e.g. This is about the HMO sub-list, not the plan types.'
                                }
                                onChange={(e) => setRedraftNote(e.target.value)}
                                autoSize={{ minRows: 2, maxRows: 6 }}
                            />
                            <div className={styles.actions}>
                                <Button
                                    type={'primary'}
                                    loading={isRedrafting}
                                    disabled={!redraftNote.trim()}
                                    onClick={() =>
                                        void redraft(redraftNote.trim())
                                    }
                                >
                                    Redraft with this note
                                </Button>
                                <Button
                                    loading={isRedrafting}
                                    onClick={() => void redraft()}
                                >
                                    Just re-roll
                                </Button>
                                <Button
                                    type={'text'}
                                    onClick={() => setShowRedraft(false)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
